import type {
  DataExportCreateScheduleInput,
  DataExportCreateScheduleMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * The generated GraphQL input types every optional field as
 * `InputMaybe<T> = T | null | undefined`, and the form feeds cleared fields
 * through as `null`. The tRPC procedure's zod schema uses `.optional()`, which
 * rejects an explicit `null`, so every nullable field is narrowed to
 * `undefined` — an omitted field and a null field mean the same thing to the
 * data-layer processor. The mapping is written out field by field rather than
 * derived, so it stays greppable against the router's input schema; the explicit
 * `NonNullFields` return type keeps that honest — a misspelled or missing key
 * fails the build rather than silently dropping a credential. Empty
 * strings are passed through untouched: V2 sent those to the rest-api Lambda
 * verbatim, so keeping them is the parity-preserving choice.
 */
type NonNullFields<T> = { [K in keyof T]: Exclude<T[K], null> };

const mapInputToTrpc = (
  input: DataExportCreateScheduleInput
): NonNullFields<DataExportCreateScheduleInput> => ({
  frequency: input.frequency,
  startDate: input.startDate ?? undefined,
  endDate: input.endDate ?? undefined,
  storageType: input.storageType,
  // Azure Blob Storage
  sasToken: input.sasToken ?? undefined,
  accountName: input.accountName ?? undefined,
  containerName: input.containerName ?? undefined,
  // AWS S3
  bucketName: input.bucketName ?? undefined,
  s3Folder: input.s3Folder ?? undefined,
  accessKey: input.accessKey ?? undefined,
  secretAccessKey: input.secretAccessKey ?? undefined,
  // SFTP
  hostname: input.hostname ?? undefined,
  port: input.port ?? undefined,
  username: input.username ?? undefined,
  password: input.password ?? undefined,
  sftpFolder: input.sftpFolder ?? undefined,
  // SharePoint
  entraSecretValue: input.entraSecretValue ?? undefined,
  entraTenantId: input.entraTenantId ?? undefined,
  entraClientId: input.entraClientId ?? undefined,
  sharePointSiteId: input.sharePointSiteId ?? undefined,
  sharePointDriveId: input.sharePointDriveId ?? undefined,
  spFolder: input.spFolder ?? undefined,
});

/**
 * Maps the tRPC mutation response ({ Id, message }) to the GraphQL mutation
 * response shape so consumers branching on the result do not need to know which
 * path executed. `Id` has no GraphQL counterpart (the Hasura action returned
 * only a message) and the tab reads the new schedule back through
 * `getActiveSchedule`, so it is deliberately dropped.
 */
const mapTrpcResponseToGraphQL = (data: {
  message: string;
}): DataExportCreateScheduleMutation => ({
  dataExportCreateSchedule: {
    __typename: 'DataExportCreateScheduleOutput',
    message: data.message,
  },
});

/**
 * NOTE: on the tRPC path the schedule's `Status` / `CronArn` flip
 * ASYNCHRONOUSLY. The mutation resolves once the row is written and the
 * `DATA_EXPORT_SCHEDULE_CREATED` event is emitted; a consumer then provisions
 * the EventBridge cron rule and flips the row to `active`. V2's Hasura event
 * trigger had the same eventual consistency, so consumers must not assume the
 * schedule is active when the promise resolves.
 */
export const useCreateDataExportScheduleTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dataExport.createSchedule.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataExport.getActiveSchedule.queryKey(),
        });

        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataExport.getScheduleExecutions.queryKey(),
        });
      },
    }),
  });

  return {
    createDataExportSchedule: async (
      input: DataExportCreateScheduleInput
    ): Promise<DataExportCreateScheduleMutation> => {
      const result = await mutation.mutateAsync(mapInputToTrpc(input));

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
